import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { BBC_LOGO_URL } from "@/lib/brand";
import ProfilePhotoWidget from "./ProfilePhotoWidget";
import NotificationBell from "./NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const links = [
  { label: "Home", id: "hero" },
  { label: "Schedule", id: "schedule" },
  { label: "What We Believe", id: "beliefs" },
  { label: "Salvation", id: "salvation" },
  { label: "Sermons", id: "sermons" },
  { label: "Events", id: "events" },
  { label: "Giving", id: "giving" },
  { label: "Prayer", id: "prayer" },
  { label: "Contact", id: "location" },
];

export default function Navbar({ hideGiving }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hasSermons, setHasSermons] = useState(false);
  const [hasEvents, setHasEvents] = useState(false);

  useEffect(() => {
    // Check if sermons exist
    api.get("/sermons", { params: { limit: 1 } }).then(r => setHasSermons((r.data?.total || 0) > 0)).catch(() => {});
    // Check if events exist
    api.get("/events").then(r => {
      const upcoming = (r.data || []).filter((e) => !e.date || new Date(e.date) >= new Date(new Date().toDateString()));
      setHasEvents(upcoming.length > 0);
    }).catch(() => {});
  }, []);

  const activeLinks = useMemo(() => {
    let filtered = links;
    if (hideGiving) filtered = filtered.filter(l => l.id !== "giving");
    if (!hasSermons) filtered = filtered.filter(l => l.id !== "sermons");
    if (!hasEvents) filtered = filtered.filter(l => l.id !== "events");
    return filtered;
  }, [hideGiving, hasSermons, hasEvents]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <button
          data-testid="nav-brand"
          onClick={() => scrollTo("hero")}
          className="flex min-h-11 items-center gap-2.5 rounded-lg text-[#1C201E] hover:opacity-80"
          aria-label="BBC Barani home"
        >
          <img src={BBC_LOGO_URL} alt="BBC Barani seal" className="w-8 h-8 object-contain" />
          <span className="font-serif-display text-xl tracking-tight">BBC Barani</span>
        </button>

        <div className="flex items-center gap-2 lg:gap-7">
          <nav aria-label="Primary navigation" className="hidden xl:flex items-center gap-5">
            {activeLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="min-h-11 rounded-lg px-1 text-sm text-[#1C201E] hover:text-[#2C4C3B] transition-colors duration-200"
              >
                {l.label}
              </button>
            ))}
          </nav>

          {user && <NotificationBell />}

          <div className="hidden xl:flex items-center gap-4">
            {user && <ProfilePhotoWidget user={user} compact />}
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="inline-flex min-h-11 items-center text-sm px-5 py-2 rounded-full bg-[#2C4C3B] text-white hover:bg-[#1A3326] transition-colors duration-200"
            >
              Admin
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-full text-[#1C201E] hover:bg-[#F3EFEA]"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            id="mobile-navigation"
            className="xl:hidden overflow-hidden bg-[#FAF9F6] border-t border-black/5 shadow-lg"
          >
            <nav aria-label="Mobile navigation" className="px-6 py-4 grid sm:grid-cols-2 gap-1">
              {activeLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="min-h-11 rounded-lg px-3 text-left text-[#1C201E] hover:bg-[#F3EFEA]"
                >
                  {l.label}
                </button>
              ))}
              {user && (
                <div className="flex min-h-11 items-center gap-3 px-3 py-2">
                  <ProfilePhotoWidget user={user} compact />
                  <Link to="/profile" className="text-[#1C201E]">Profile</Link>
                </div>
              )}
              <Link
                to="/admin"
                data-testid="mobile-nav-admin-link"
                className="inline-flex min-h-11 items-center justify-center px-4 py-2 rounded-full bg-[#2C4C3B] text-white"
              >
                Open CMS
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
